import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { MeResponse } from '@greeniq/shared';
import { api, ApiError } from '../api/client.js';

interface MeContextValue {
  me: MeResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const MeContext = createContext<MeContextValue | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<MeResponse>('/users/me');
      setMe(data);
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status} ${err.message}` : String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <MeContext.Provider value={{ me, loading, error, refetch: load }}>{children}</MeContext.Provider>
  );
}

export function useMe() {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error('useMe must be inside MeProvider');
  return ctx;
}
